import { Sparkles, Heart, Eye, TrendingUp, ChevronLeft } from 'lucide-react';

interface BoostPromoViewProps {
  userName?: string;
  onClose: () => void;
  onGetBoost: () => void;
  onViewPlans: () => void;
}

export function BoostPromoView({ userName, onClose, onGetBoost, onViewPlans }: BoostPromoViewProps) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: '#0F1419' }}>
      {/* Ambient background effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00FF88] opacity-15 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-[#8B5CF6] opacity-10 blur-[120px] rounded-full" />
      </div>

      {/* Header */}
      <div className="relative z-10 px-6 pt-6 pb-4 flex items-center justify-between border-b backdrop-blur-xl"
           style={{
             background: 'rgba(255, 255, 255, 0.03)',
             borderColor: 'rgba(255, 255, 255, 0.1)',
           }}>
        <button
          onClick={onClose}
          className="flex items-center gap-2 transition-opacity hover:opacity-70"
        >
          <ChevronLeft className="w-6 h-6" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Назад</span>
        </button>
        <h1 className="bg-gradient-to-r from-[#00FF88] via-[#00FF88] to-[#8B5CF6] bg-clip-text text-transparent">
          Splyt
        </h1>
        <div className="w-[72px]"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 overflow-y-auto">
        <div className="min-h-full flex flex-col items-center justify-center px-6 py-12">
          {/* User Name */}
          {userName && (
            <p className="mb-8" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              {userName}
            </p>
          )}

          {/* Main Headline */}
          <h2 className="text-center mb-6 max-w-2xl"
              style={{ 
                color: '#FFFFFF',
                fontSize: 'clamp(32px, 8vw, 56px)',
                lineHeight: '1.2',
              }}>
            Get 3 months of Premium for free
          </h2>

          {/* Subheadline */}
          <p className="text-center mb-12 max-w-md"
             style={{ 
               color: 'rgba(255, 255, 255, 0.7)',
               fontSize: '18px',
             }}>
            Enjoy endless ad-free matching - even offline.
          </p>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 w-full max-w-3xl">
            <div className="p-4 rounded-2xl border backdrop-blur-xl"
                 style={{
                   background: 'rgba(0, 255, 136, 0.05)',
                   borderColor: 'rgba(0, 255, 136, 0.2)',
                 }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                   style={{
                     background: 'rgba(0, 255, 136, 0.2)',
                     border: '1px solid rgba(0, 255, 136, 0.3)',
                   }}>
                <TrendingUp className="w-6 h-6" style={{ color: '#00FF88' }} />
              </div>
              <h3 style={{ color: '#FFFFFF', marginBottom: '4px' }}>x3 Visibility</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                Get 3x more profile views
              </p>
            </div>

            <div className="p-4 rounded-2xl border backdrop-blur-xl"
                 style={{
                   background: 'rgba(0, 255, 136, 0.05)',
                   borderColor: 'rgba(0, 255, 136, 0.2)',
                 }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                   style={{
                     background: 'rgba(0, 255, 136, 0.2)',
                     border: '1px solid rgba(0, 255, 136, 0.3)',
                   }}>
                <Heart className="w-6 h-6" style={{ color: '#00FF88' }} />
              </div>
              <h3 style={{ color: '#FFFFFF', marginBottom: '4px' }}>Unlimited Likes</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                Like as many profiles as you want
              </p>
            </div>

            <div className="p-4 rounded-2xl border backdrop-blur-xl"
                 style={{
                   background: 'rgba(0, 255, 136, 0.05)',
                   borderColor: 'rgba(0, 255, 136, 0.2)',
                 }}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                   style={{
                     background: 'rgba(0, 255, 136, 0.2)',
                     border: '1px solid rgba(0, 255, 136, 0.3)',
                   }}>
                <Eye className="w-6 h-6" style={{ color: '#00FF88' }} />
              </div>
              <h3 style={{ color: '#FFFFFF', marginBottom: '4px' }}>See Who Likes You</h3>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                Know who&apos;s interested instantly
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 w-full max-w-md">
            <button
              onClick={onGetBoost}
              className="flex-1 px-8 py-4 rounded-full transition-all hover:scale-105 uppercase tracking-wider"
              style={{
                background: '#00FF88',
                color: '#0F1419',
                boxShadow: '0 8px 24px rgba(0, 255, 136, 0.4)',
              }}
            >
              Get 3 Months Free
            </button>
            
            <button
              onClick={onViewPlans}
              className="flex-1 px-8 py-4 rounded-full border-2 transition-all hover:bg-white/5 uppercase tracking-wider"
              style={{
                borderColor: '#00FF88',
                color: '#00FF88',
              }}
            >
              View Plans
            </button>
          </div>

          {/* Terms */}
          <p className="text-center max-w-2xl"
             style={{ 
               color: 'rgba(255, 255, 255, 0.4)',
               fontSize: '12px',
               lineHeight: '1.5',
             }}>
            Monthly subscription fee applies after. Offer not available to users who already tried Premium.{' '}
            <a href="#" className="underline hover:opacity-80" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              terms and conditions apply
            </a>
            . Offer ends Jun 30, 2024.
          </p>
        </div>
      </div>

      {/* Decorative Sparkle */}
      <div className="fixed bottom-8 right-8 pointer-events-none">
        <Sparkles 
          className="w-16 h-16 animate-pulse" 
          style={{ 
            color: '#00FF88',
            filter: 'drop-shadow(0 0 20px rgba(0, 255, 136, 0.6))',
            opacity: 0.3,
          }} 
        />
      </div>
    </div>
  );
}
